import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { cn } from "../../lib/utils";

export interface DatePickerProps {
  value?: string; // YYYY-MM-DD (date) or YYYY-MM-DDTHH:mm (datetime)
  onChange: (val: string) => void;
  maxDate?: string;
  minDate?: string;
  placeholder?: string;
  className?: string;
  align?: "left" | "right";
  allowClear?: boolean;
  disabled?: boolean;
  mode?: "date" | "datetime";
}

function parseLocalDate(str?: string): Date | null {
  if (!str) return null;
  // Handle ISO or YYYY-MM-DD or YYYY-MM-DDTHH:mm
  const datePart = str.split("T")[0];
  const timePart = str.includes("T") ? str.split("T")[1] : null;
  const parts = datePart.split("-");
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;

  const dateObj = new Date(y, m, d);
  if (timePart) {
    const [hh, mm] = timePart.split(":");
    dateObj.setHours(parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, 0, 0);
  }
  return dateObj;
}

function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toLocalDateTimeString(d: Date, hours: number, minutes: number): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function DatePicker({
  value,
  onChange,
  maxDate,
  minDate,
  placeholder,
  className,
  align = "left",
  allowClear = false,
  disabled = false,
  mode = "date",
}: DatePickerProps) {
  const isDateTime = mode === "datetime";
  const defaultPlaceholder = isDateTime ? "Select date & time" : "Select date";
  const labelPlaceholder = placeholder || defaultPlaceholder;

  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});

  const selectedDate = parseLocalDate(value);
  const maxD = parseLocalDate(maxDate);
  const minD = parseLocalDate(minDate);

  const [viewMonth, setViewMonth] = useState<Date>(() => {
    return selectedDate || new Date();
  });

  // Time state for datetime mode (default to 09:00 or current selected time)
  const [hours, setHours] = useState<number>(() => {
    return selectedDate ? selectedDate.getHours() : 9;
  });
  const [minutes, setMinutes] = useState<number>(() => {
    return selectedDate ? selectedDate.getMinutes() : 0;
  });

  // When value changes externally, align viewMonth and time
  useEffect(() => {
    const parsed = parseLocalDate(value);
    if (parsed) {
      setViewMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
      if (isDateTime) {
        setHours(parsed.getHours());
        setMinutes(parsed.getMinutes());
      }
    }
  }, [value, isDateTime]);

  // Position calendar menu via fixed coordinates so it can NEVER be clipped by modals or scroll containers
  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;

    const POPOVER_W = 280;
    const POPOVER_H = isDateTime ? 375 : 280;

    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;

    // Prefer dropping down unless there is clearly more room above
    const openUp = spaceBelow < POPOVER_H && spaceAbove > spaceBelow;

    let left = align === "right" ? r.right - POPOVER_W : r.left;
    if (left + POPOVER_W > window.innerWidth - 8) {
      left = window.innerWidth - POPOVER_W - 8;
    }
    if (left < 8) {
      left = 8;
    }

    const availableSpace = openUp ? spaceAbove - 8 : spaceBelow - 8;
    const maxHeight = Math.max(220, Math.min(POPOVER_H, availableSpace));

    setPopoverStyle({
      position: "fixed",
      left: `${left}px`,
      width: `${POPOVER_W}px`,
      ...(openUp
        ? { bottom: `${window.innerHeight - r.top + 4}px` }
        : { top: `${r.bottom + 4}px` }),
      maxHeight: `${maxHeight}px`,
      zIndex: 9999, // Float above modal overlay (z-50) and any other popups
    });
  }, [align, isDateTime]);

  // Keep position pinned during scroll and resize
  useEffect(() => {
    if (!open) return;
    place();

    const handleScrollOrResize = () => place();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open, place]);

  // Dismiss on click outside
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        btnRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener("mousedown", handlePointerDown, true);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown, true);
    };
  }, [open]);

  // Dismiss on Escape
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    if (!open) {
      place();
    }
    setOpen((o) => !o);
  };

  const formattedLabel = selectedDate
    ? isDateTime
      ? selectedDate.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }) +
        ", " +
        selectedDate.toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : selectedDate.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
    : labelPlaceholder;

  const y = viewMonth.getFullYear();
  const m = viewMonth.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();

  const grid: (Date | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    grid.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    grid.push(new Date(y, m, d));
  }

  const prevMonth = () => setViewMonth(new Date(y, m - 1, 1));
  const nextMonth = () => setViewMonth(new Date(y, m + 1, 1));

  const today = new Date();

  const handleDayClick = (d: Date) => {
    if (isDateTime) {
      onChange(toLocalDateTimeString(d, hours, minutes));
    } else {
      onChange(toLocalDateString(d));
      setOpen(false);
    }
  };

  const handleTimeChange = (newHours: number, newMinutes: number) => {
    setHours(newHours);
    setMinutes(newMinutes);
    const base = selectedDate || today;
    onChange(toLocalDateTimeString(base, newHours, newMinutes));
  };

  return (
    <div className="relative inline-block w-full">
      {/* Trigger Button */}
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-lg border border-line dark:border-white/10 bg-white dark:bg-[#1a1816] px-3 py-2 text-xs font-semibold text-ink dark:text-stone-200 shadow-2xs hover:bg-stone-50 dark:hover:bg-white/5 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed",
          !selectedDate && "text-ink-3 dark:text-stone-500 font-normal",
          className,
        )}
      >
        <div className="flex items-center gap-2 truncate">
          <CalendarIcon className="h-3.5 w-3.5 text-brand shrink-0" />
          <span className="truncate">{formattedLabel}</span>
        </div>
        <ChevronDown className={cn("h-3.5 w-3.5 text-ink-3 shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      {/* Portaled Popover Menu (renders in document.body so it is NEVER clipped by modal/scroll boundaries) */}
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            style={popoverStyle}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex flex-col overflow-y-auto rounded-xl border border-line dark:border-white/10 bg-white dark:bg-[#201e1b] p-3 shadow-2xl animate-in fade-in zoom-in-95 duration-100"
          >
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={prevMonth}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs font-semibold text-ink dark:text-white">
                {viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
              </span>
              <button
                type="button"
                onClick={nextMonth}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Weekday Labels */}
            <div className="grid grid-cols-7 mb-1 text-center font-mono text-[10px] font-semibold text-ink-3 dark:text-stone-400">
              {WEEKDAYS.map((w) => (
                <span key={w} className="py-0.5">
                  {w}
                </span>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-0.5">
              {grid.map((d, idx) => {
                if (!d) {
                  return <span key={`empty-${idx}`} className="h-7 w-7" />;
                }

                const isSelected = !!selectedDate && isSameDay(d, selectedDate);
                const isToday = isSameDay(d, today);
                const isFuture = !!maxD && d > maxD;
                const isPast = !!minD && d < minD;
                const isDisabled = isFuture || isPast;

                return (
                  <button
                    type="button"
                    key={d.toISOString()}
                    disabled={isDisabled}
                    onClick={() => handleDayClick(d)}
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-lg text-xs font-medium transition-colors mx-auto",
                      isSelected
                        ? "bg-brand text-white font-bold shadow-xs hover:bg-brand"
                        : isToday
                        ? "border border-brand text-brand font-semibold hover:bg-stone-100 dark:hover:bg-white/5"
                        : isDisabled
                        ? "text-stone-300 dark:text-stone-600 cursor-not-allowed opacity-40 hover:bg-transparent"
                        : "text-ink dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-white/5",
                    )}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>

            {/* Time Picker Controls (for mode="datetime") */}
            {isDateTime && (
              <div className="mt-2.5 pt-2 border-t border-line dark:border-white/10 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 text-ink-3 dark:text-stone-400 font-medium">
                    <Clock className="h-3.5 w-3.5 text-brand" />
                    <span>Time</span>
                  </div>

                  <div className="flex items-center gap-1">
                    {/* Hours Select */}
                    <select
                      value={hours}
                      onChange={(e) => handleTimeChange(parseInt(e.target.value, 10), minutes)}
                      className="rounded border border-line dark:border-white/10 bg-stone-50 dark:bg-stone-800 px-1.5 py-1 text-xs font-mono font-semibold text-ink dark:text-white outline-none focus:border-brand"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {String(i).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <span className="font-mono text-ink-3">:</span>
                    {/* Minutes Select */}
                    <select
                      value={minutes}
                      onChange={(e) => handleTimeChange(hours, parseInt(e.target.value, 10))}
                      className="rounded border border-line dark:border-white/10 bg-stone-50 dark:bg-stone-800 px-1.5 py-1 text-xs font-mono font-semibold text-ink dark:text-white outline-none focus:border-brand"
                    >
                      {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                        <option key={m} value={m}>
                          {String(m).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Quick Time Presets */}
                <div className="flex flex-wrap gap-1 pt-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      handleTimeChange(now.getHours(), now.getMinutes());
                    }}
                    className="rounded px-2 py-0.5 text-[10px] font-medium bg-stone-100 dark:bg-stone-800 text-ink-2 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                  >
                    Now
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTimeChange(10, 0)}
                    className="rounded px-2 py-0.5 text-[10px] font-medium bg-stone-100 dark:bg-stone-800 text-ink-2 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                  >
                    10:00 AM
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTimeChange(14, 30)}
                    className="rounded px-2 py-0.5 text-[10px] font-medium bg-stone-100 dark:bg-stone-800 text-ink-2 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                  >
                    2:30 PM
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTimeChange(18, 0)}
                    className="rounded px-2 py-0.5 text-[10px] font-medium bg-stone-100 dark:bg-stone-800 text-ink-2 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                  >
                    6:00 PM
                  </button>
                </div>
              </div>
            )}

            {/* Bottom Shortcuts / Done */}
            <div className="mt-2 pt-2 border-t border-line dark:border-white/10 flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => {
                  if (isDateTime) {
                    onChange(toLocalDateTimeString(today, hours, minutes));
                  } else {
                    onChange(toLocalDateString(today));
                    setOpen(false);
                  }
                  setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                }}
                className="text-[11px] font-semibold text-brand hover:underline"
              >
                Today
              </button>

              <div className="flex items-center gap-2">
                {allowClear && selectedDate && (
                  <button
                    type="button"
                    onClick={() => {
                      onChange("");
                      setOpen(false);
                    }}
                    className="text-[11px] text-ink-3 dark:text-stone-400 hover:text-red-500 transition-colors"
                  >
                    Clear
                  </button>
                )}
                {isDateTime && (
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md bg-brand px-2.5 py-1 text-[11px] font-semibold text-white shadow-xs hover:bg-brand-hover transition-colors"
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export function DateTimePicker(props: Omit<DatePickerProps, "mode">) {
  return <DatePicker {...props} mode="datetime" />;
}
